import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Download, Play, ArrowRight, Star, Users, Clock } from "lucide-react";

const AboutCTA = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const stats = [
    { icon: Users, value: "10K+", label: "Active Users" },
    { icon: Star, value: "4.9", label: "App Rating" },
    { icon: Clock, value: "50+", label: "Sessions Available" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-32 bg-white overflow-hidden"
    >
      {/* Floating decorative elements */}
      <motion.div
        className="absolute right-10 top-20 w-40 h-40 rounded-full bg-gradient-to-br from-soft-saffron/20 to-copper-brown/20 blur-3xl hidden lg:block"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute left-10 bottom-20 w-32 h-32 rounded-full bg-gradient-to-tr from-forest-green/20 to-misty-sage/20 blur-2xl hidden lg:block"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.5, 0.2],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-8">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-pale-blue/20 border border-pale-blue/30 backdrop-blur-sm">
                <Download className="w-5 h-5 text-soft-saffron" />
                <span className="text-sm font-medium text-forest-green uppercase tracking-wider">
                  Start Your Journey
                </span>
              </div>
            </motion.div>

            {/* Main Heading */}
            <motion.h2
              variants={itemVariants}
              className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-forest-green mb-8 drop-shadow-lg"
            >
              Ready to Begin Your{" "}
              <span className="text-pale-blue">Journey Inward?</span>
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-xl md:text-2xl text-copper-brown/90 mb-12 leading-relaxed max-w-4xl mx-auto font-light"
            >
              Experience the transformative power of Yoga Nidra, wherever you
              are. Join thousands who have discovered inner peace through
              conscious rest and ancient wisdom.
            </motion.p>

            {/* Download Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
            >
              {/* App Store Button */}
              <button className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-3xl">
                <div className="bg-black text-white px-8 py-4 flex items-center gap-4 rounded-xl border border-gray-600 hover:border-gray-400 transition-all duration-300">
                  <img
                    src="/apple.svg"
                    alt="Apple Logo"
                    className="w-12 h-12"
                  />
                  <div className="text-left">
                    <div className="text-xs text-gray-300">Download on the</div>
                    <div className="text-lg font-semibold leading-tight">
                      App Store
                    </div>
                  </div>
                </div>
              </button>

              {/* Google Play Button */}
              <button className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 shadow-2xl hover:shadow-3xl">
                <div className="bg-black text-white px-8 py-4 flex items-center gap-4 rounded-xl border border-gray-600 hover:border-gray-400 transition-all duration-300">
                  <img
                    src="/google-play.svg"
                    alt="Google Play Logo"
                    className="w-12 h-12"
                  />
                  <div className="text-left">
                    <div className="text-xs text-gray-300">GET IT ON</div>
                    <div className="text-lg font-semibold leading-tight">
                      Google Play
                    </div>
                  </div>
                </div>
              </button>
            </motion.div>

            {/* Secondary CTA */}
          </motion.div>

          {/* Bottom Message */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            transition={{ delay: 1 }}
            className="text-center mt-16"
          >
            <p className="text-copper-brown/90 text-lg max-w-2xl mx-auto">
              "Every journey of a thousand miles begins with a single step. Take
              yours today and discover the profound peace that awaits within."
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutCTA;
